import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const EXAMPLE_INPUT = "4539 1488 0343 6467";

// Prefix table for best-effort card brand detection. Order matters —
// first match wins, so narrower prefixes (e.g. Discover's 65) are listed
// after their broader siblings. All ranges here are inclusive.
const CARD_BRANDS = [
  { key: "visa", labelKey: "luhn_brand_visa", lengths: [13, 16, 19], match: (d) => d.startsWith("4") },
  {
    key: "mastercard",
    labelKey: "luhn_brand_mastercard",
    lengths: [16],
    match: (d) => {
      const two = parseInt(d.slice(0, 2), 10);
      const four = parseInt(d.slice(0, 4), 10);
      return (two >= 51 && two <= 55) || (four >= 2221 && four <= 2720);
    },
  },
  {
    key: "amex",
    labelKey: "luhn_brand_amex",
    lengths: [15],
    match: (d) => d.startsWith("34") || d.startsWith("37"),
  },
  {
    key: "discover",
    labelKey: "luhn_brand_discover",
    lengths: [16, 19],
    match: (d) => {
      const three = parseInt(d.slice(0, 3), 10);
      const six = parseInt(d.slice(0, 6), 10);
      return (
        d.startsWith("6011") ||
        d.startsWith("65") ||
        (three >= 644 && three <= 649) ||
        (six >= 622126 && six <= 622925)
      );
    },
  },
  {
    key: "diners",
    labelKey: "luhn_brand_diners",
    lengths: [14, 16, 19],
    match: (d) => {
      const two = parseInt(d.slice(0, 2), 10);
      const three = parseInt(d.slice(0, 3), 10);
      return (
        d.startsWith("36") ||
        d.startsWith("39") ||
        (two === 54 || two === 55) ||
        (three >= 300 && three <= 305)
      );
    },
  },
  {
    key: "jcb",
    labelKey: "luhn_brand_jcb",
    lengths: [16, 17, 18, 19],
    match: (d) => {
      const four = parseInt(d.slice(0, 4), 10);
      return four >= 3528 && four <= 3589;
    },
  },
  {
    key: "unionpay",
    labelKey: "luhn_brand_unionpay",
    lengths: [16, 17, 18, 19],
    match: (d) => d.startsWith("62"),
  },
];

const detectBrand = (digits) => {
  if (!digits) return null;
  for (const brand of CARD_BRANDS) {
    if (!brand.match(digits)) continue;
    const expected = brand.lengths.includes(digits.length);
    return { key: brand.key, labelKey: brand.labelKey, lengthMatches: expected, lengths: brand.lengths };
  }
  return null;
};

const stripNonDigits = (raw) => (raw || "").replace(/\D+/g, "");

// Doubled-digit lookup for even positions (0-indexed from the right, not
// counting the check digit). Written out so there's no branch per digit —
// Luhn is simple enough that readability > cleverness here.
const DOUBLED = [0, 2, 4, 6, 8, 1, 3, 5, 7, 9];

// Sum of digits weighted per the Luhn algorithm. `includesCheck` tells
// whether `digits` includes the trailing check digit (validate) or not
// (generate). Luhn doubles every digit at an even position counted from
// the right of the full number (check digit at pos 1), so in both cases
// the first iteration from `start` is doubled.
const luhnSum = (digits, includesCheck) => {
  let sum = 0;
  const start = includesCheck ? digits.length - 2 : digits.length - 1;
  let double = true;
  for (let i = start; i >= 0; i -= 1) {
    const n = digits.charCodeAt(i) - 48;
    sum += double ? DOUBLED[n] : n;
    double = !double;
  }
  return sum;
};

const computeCheckDigit = (digitsNoCheck) => {
  if (!digitsNoCheck) return null;
  const sum = luhnSum(digitsNoCheck, false);
  return (10 - (sum % 10)) % 10;
};

const validate = (digits) => {
  if (!digits) return { empty: true };
  if (digits.length < 2) return { valid: false, tooShort: true };
  const sum = luhnSum(digits, true);
  const check = digits.charCodeAt(digits.length - 1) - 48;
  const expected = (10 - (sum % 10)) % 10;
  return {
    valid: (sum + check) % 10 === 0,
    actualCheck: check,
    expectedCheck: expected,
  };
};

// Group digits in blocks for readability. 4-4-4-4 (and 4-4-4-3 for Amex,
// 4-6-5 for 15-digit non-Amex like IMEI) are the formats people expect.
const groupDigits = (digits, brandKey) => {
  if (!digits) return "";
  if (brandKey === "amex" && digits.length === 15) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 10)} ${digits.slice(10)}`;
  }
  if (digits.length === 15) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 8)} ${digits.slice(8, 14)} ${digits.slice(14)}`;
  }
  const groups = [];
  for (let i = 0; i < digits.length; i += 4) {
    groups.push(digits.slice(i, i + 4));
  }
  return groups.join(" ");
};

const likelyUsage = (digits, brand) => {
  if (!digits) return null;
  if (brand && brand.lengthMatches) return brand.labelKey;
  if (digits.length === 15) return "luhn_usage_imei";
  if (digits.length === 12) return "luhn_usage_isin_or_npi";
  if (digits.length === 10) return "luhn_usage_npi";
  if (digits.length === 9) return "luhn_usage_sin";
  if (digits.length === 8) return "luhn_usage_short";
  return null;
};

const LuhnValidator = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState("validate");
  const [input, setInput] = useState(EXAMPLE_INPUT);
  const digits = useMemo(() => stripNonDigits(input), [input]);

  const brand = useMemo(() => detectBrand(digits), [digits]);
  const usageKey = useMemo(() => likelyUsage(digits, brand), [digits, brand]);
  const grouped = useMemo(() => groupDigits(digits, brand?.key), [digits, brand]);

  const result = useMemo(() => validate(digits), [digits]);
  const generated = useMemo(() => {
    if (mode !== "generate" || !digits) return null;
    const check = computeCheckDigit(digits);
    if (check == null) return null;
    const full = digits + String(check);
    return { check, full, grouped: groupDigits(full, detectBrand(full)?.key) };
  }, [mode, digits]);

  const copy = async (text, label) => {
    if (!text) {
      toast.error(t("luhn_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label ? `${label} ${t("copy_success")}` : t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const loadExample = () => {
    setInput(mode === "generate" ? "453914880343646" : EXAMPLE_INPUT);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("luhn_title")}
            </h1>
            <p className="text-gray-600">{t("luhn_intro")}</p>

            <div
              role="radiogroup"
              aria-label={t("luhn_mode_label")}
              className="inline-flex rounded-md border border-indigo-200 overflow-hidden self-start"
            >
              <button
                type="button"
                role="radio"
                aria-checked={mode === "validate"}
                onClick={() => setMode("validate")}
                className={`px-4 py-2 text-sm font-medium transition ${
                  mode === "validate"
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-indigo-700 hover:bg-indigo-50"
                }`}
              >
                {t("luhn_mode_validate")}
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={mode === "generate"}
                onClick={() => setMode("generate")}
                className={`px-4 py-2 text-sm font-medium transition border-l border-indigo-200 ${
                  mode === "generate"
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-indigo-700 hover:bg-indigo-50"
                }`}
              >
                {t("luhn_mode_generate")}
              </button>
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="luhn-input"
                className="text-sm font-medium text-gray-700"
              >
                {mode === "validate"
                  ? t("luhn_input_label_validate")
                  : t("luhn_input_label_generate")}
              </label>
              <input
                id="luhn-input"
                type="text"
                inputMode="numeric"
                spellCheck={false}
                autoComplete="off"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={
                  mode === "validate"
                    ? t("luhn_input_placeholder_validate")
                    : t("luhn_input_placeholder_generate")
                }
                className="w-full p-3 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                aria-invalid={
                  mode === "validate" && result && result.valid === false && !result.empty && !result.tooShort
                    ? "true"
                    : "false"
                }
              />
              <p className="text-xs text-gray-500">
                {t("luhn_input_hint", { count: digits.length })}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <StandardButton
                onClick={loadExample}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("luhn_load_example")}
              </StandardButton>
              <StandardButton
                onClick={() => setInput("")}
                type="secondary"
                variant="outlined"
                size="small"
                disabled={!input}
              >
                {t("luhn_clear")}
              </StandardButton>
              <StandardButton
                onClick={async () => {
                  try {
                    const text = await navigator.clipboard.readText();
                    setInput(text);
                  } catch {
                    toast.error(t("failed_to_copy"));
                  }
                }}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("luhn_paste")}
              </StandardButton>
            </div>

            {result.empty && (
              <p className="text-sm text-gray-500 italic">
                {mode === "validate"
                  ? t("luhn_empty_hint_validate")
                  : t("luhn_empty_hint_generate")}
              </p>
            )}

            {mode === "validate" && !result.empty && result.tooShort && (
              <div
                role="alert"
                className="bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-md text-sm"
              >
                {t("luhn_too_short")}
              </div>
            )}

            {mode === "validate" && !result.empty && !result.tooShort && (
              <div
                role="status"
                aria-live="polite"
                className={`p-4 rounded-md border ${
                  result.valid
                    ? "bg-green-50 border-green-200 text-green-800"
                    : "bg-red-50 border-red-200 text-red-700"
                }`}
              >
                <div className="font-semibold">
                  {result.valid ? t("luhn_valid") : t("luhn_invalid")}
                </div>
                {!result.valid && (
                  <div className="text-sm mt-1">
                    {t("luhn_invalid_detail", {
                      actual: result.actualCheck,
                      expected: result.expectedCheck,
                    })}
                  </div>
                )}
              </div>
            )}

            {mode === "generate" && generated && (
              <div
                role="status"
                aria-live="polite"
                className="bg-indigo-50 border border-indigo-200 p-4 rounded-md"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wide text-indigo-700 font-medium">
                      {t("luhn_generate_check_digit")}
                    </div>
                    <div className="font-mono text-2xl text-indigo-900 mt-1">
                      {generated.check}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-wide text-indigo-700 font-medium">
                      {t("luhn_generate_full")}
                    </div>
                    <div className="font-mono text-base break-all text-gray-900 mt-1">
                      {generated.grouped}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <StandardButton
                      onClick={() => copy(generated.full, t("luhn_generate_full"))}
                      type="secondary"
                      variant="outlined"
                      size="small"
                    >
                      {t("copy_button")}
                    </StandardButton>
                    <StandardButton
                      onClick={() => {
                        setMode("validate");
                        setInput(generated.full);
                      }}
                      type="secondary"
                      variant="outlined"
                      size="small"
                    >
                      {t("luhn_generate_validate_this")}
                    </StandardButton>
                  </div>
                </div>
              </div>
            )}

            {digits && (
              <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col gap-2">
                <div className="grid grid-cols-12 gap-2 items-start">
                  <div className="col-span-12 sm:col-span-4 text-xs uppercase tracking-wide text-indigo-700 font-medium pt-1">
                    {t("luhn_field_digits")}
                  </div>
                  <div className="col-span-12 sm:col-span-7 font-mono text-sm break-all text-gray-800">
                    {grouped}
                  </div>
                  <div className="col-span-12 sm:col-span-1 flex sm:justify-end">
                    <StandardButton
                      onClick={() => copy(digits, t("luhn_field_digits"))}
                      type="secondary"
                      variant="outlined"
                      size="small"
                      aria-label={`${t("copy_button")} — ${t("luhn_field_digits")}`}
                    >
                      {t("copy_button")}
                    </StandardButton>
                  </div>
                </div>

                <div className="grid grid-cols-12 gap-2 items-start border-t border-gray-100 pt-2">
                  <div className="col-span-12 sm:col-span-4 text-xs uppercase tracking-wide text-indigo-700 font-medium pt-1">
                    {t("luhn_field_length")}
                  </div>
                  <div className="col-span-12 sm:col-span-8 text-sm text-gray-800">
                    {digits.length}
                  </div>
                </div>

                {brand && (
                  <div className="grid grid-cols-12 gap-2 items-start border-t border-gray-100 pt-2">
                    <div className="col-span-12 sm:col-span-4 text-xs uppercase tracking-wide text-indigo-700 font-medium pt-1">
                      {t("luhn_field_brand")}
                    </div>
                    <div className="col-span-12 sm:col-span-8 text-sm text-gray-800">
                      {t(brand.labelKey)}
                      {!brand.lengthMatches && (
                        <span className="ml-2 inline-block text-xs bg-amber-100 text-amber-800 border border-amber-200 rounded px-2 py-0.5">
                          {t("luhn_brand_length_mismatch", {
                            lengths: brand.lengths.join(", "),
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {!brand && usageKey && (
                  <div className="grid grid-cols-12 gap-2 items-start border-t border-gray-100 pt-2">
                    <div className="col-span-12 sm:col-span-4 text-xs uppercase tracking-wide text-indigo-700 font-medium pt-1">
                      {t("luhn_field_likely_use")}
                    </div>
                    <div className="col-span-12 sm:col-span-8 text-sm text-gray-800">
                      {t(usageKey)}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="border-t border-gray-200 pt-4 mt-2 text-xs text-gray-500 leading-relaxed">
              <p>{t("luhn_note_spec")}</p>
              <p>{t("luhn_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default LuhnValidator;
